import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ModuleStaff, RoleType, UserType } from "@maysoft/common-component-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Helpers from "commons/helpers";
import Constants from "constants/index";
import Screens from "constants/screens";
import Strings from "constants/strings";

import { RootState } from "store";
import { BooleanInt, Mode } from "constants/enum";
import { DashboardLayout } from "layout";
import { setListPathName } from "store/slice/titleRoute.slice";


interface IRequestGetPaged {
    orderby?: string;
    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
    searchText?: string;
    listStatus?: any[];
    fullName?: string;
    roleCode?: string;
    groupId?: string;
    isAll?: boolean;
    nonHierarchical?: BooleanInt;
}

const UserScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const id = searchParams.get("id");
    const modeList = (Screens.USER_LIST.includes(window.location.pathname));
    const mode = !Helpers.isNullOrEmpty(searchParams.get("mode")) ? Number(searchParams.get("mode")) : undefined;

    const listPathName = useSelector((state: RootState) => state.titleRoute.listPathName);

    const [model, setModel] = useState<any>({});

    const requestData = useMemo(() => {
        return {
            groupId: searchParams.get("groupId"),
            roleCode: searchParams.get("roleCode"),
            fullName: searchParams.get("fullName"),
            searchText: searchParams.get("searchText"),
            listStatus: searchParams.getAll("listStatus"),
            pageNumber: Number(searchParams.get("pageNumber") || 1),
            pageSize: Number(searchParams.get("pageSize") || Constants.ROW_PER_PAGE),
            nonHierarchical: BooleanInt.False
        } as IRequestGetPaged;
    }, [searchParams])

    useEffect(() => {
        if (Helpers.isNullOrEmpty(id)) {
            handleChangeMode(Mode.Create);
        } else {
            handleChangeMode(mode ?? Mode.Update);
        }
    }, [id, mode]);

    const handleGoBack = () => {
        const itemP = listPathName.find((el) => el.pathName === Screens.USER_LIST);
        navigate(Screens.USER_LIST + (itemP ? itemP.query : ""));
    };

    const handleChangeMode = (value: Mode) => {
        let title: string = "";
        let route = [{ title: Strings.USER.TITLE_MENU, route: Screens.USER_LIST }];

        if (value === Mode.Create) {
            title = Strings.USER.TITLE_CREATE_VIEW;
            route = [
                { title: Strings.USER.TITLE_MENU, route: Screens.USER_LIST },
                { title: Strings.COMMON.ADD_NEW, route: "" },
            ];
        }
        if (value === Mode.Update) {
            title = Strings.USER.TITLE_UPDATE_VIEW;
            route = [
                { title: Strings.USER.TITLE_MENU, route: Screens.USER_LIST },
                { title: Strings.COMMON.UPDATE, route: "", },
            ];
        }
        if (value === Mode.View) {
            title = Strings.USER.TITLE_DETAIL_VIEW;
            route = [
                { title: Strings.USER.TITLE_MENU, route: Screens.USER_LIST },
                { title: Strings.COMMON.VIEW_DETAIL, route: "", },
            ];
        }

        setModel({
            ...model,
            mode: value,
            title: title,
            route: route,
        });

        if (!Helpers.isNullOrEmpty(id)) {
            navigate(Screens.USER_DETAIL + `?id=${id}&mode=${value}`, { replace: true });
        }
    }

    return (
        <DashboardLayout
            isPermission
            allowSettingLayout
            requiredOrganization
            title={modeList ? Strings.USER.TITLE_LIST_VIEW : model.title}
            route={modeList ? [{ title: Strings.USER.TITLE_MENU, route: "" }] : model.route}
        >
            <ModuleStaff
                keyModuleContainer={{
                    list: { pathName: Screens.USER_LIST },
                    detail: { pathName: Screens.USER_DETAIL },
                    create: { pathName: Screens.USER_CREATE },
                }}

                list={{
                    hiddenRoleTypes: [RoleType.Default | RoleType.User]
                }}
                create={{
                    userType: UserType.Managed,
                    isActive: true,
                }}
                modalCreateUpdateMember={{
                    userType: UserType.Managed,
                    createUserActive: true
                }}

                userRole={{
                    ignoreHierarchy: BooleanInt.True
                }}

                isNavigate
                nonHierarchical
                showLoginLink
                groupLabel={Strings.COMMON.DEPARTMENT}
                staffLabel={Strings.COMMON.STAFF}

                allowActionResetPassword
                allowActionImport
                allowActionExport
                importTemplateLink={Constants.IMPORT_USER_TEMPLATE_LINK}

                getGroupNonHierarchical
                requestGetPaged={requestData}
                onGetPaged={async ({ query, totalCount }) => {
                    dispatch(setListPathName({ pathname: Screens.USER_LIST, query, totalCount }));
                }}
                onNavigate={async (data) => {
                    if (data.id) {
                        navigate(Screens.USER_DETAIL + `?id=${data.id}&mode=${data.mode}`);
                    } else {
                        navigate(Screens.USER_CREATE);
                    }
                }}
                idDetail={id}
                mode={model.mode}
                onGoBack={handleGoBack}
                onChangeMode={(value) => { handleChangeMode(value) }}
                hiddenIdCardInfo
                hiddenPassportInfo
            />
        </DashboardLayout>
    );
};

export default UserScreen;
